import { Link, useSearchParams } from "react-router-dom";
import type { AdminOpenClaw } from "../../../types/openclaw";
import type { TenantHealthTimeline, TenantMetrics, TenantToolCallHistory, TenantUsageHistory } from "../../../hooks/useAdminStatus";
import { useRestartHistory, type RestartHistory } from "../../../hooks/useRestartHistory";
import { StatCard, type StatTone } from "../StatCard";
import { isUnmeteredAi } from "../../../lib/aiSpend";
import { attentionCoverage, attentionItems, type AttentionCoverage, type AttentionItem, type SourceStatus } from "../../../lib/instanceAttention";
import { rangeShortLabel, rangeWindowLabel, type InstanceRange } from "../../../lib/instanceDetailRange";
import styles from "./instanceDetail.module.css";

type OverviewProps = {
  readonly claw: AdminOpenClaw;
  readonly range: InstanceRange;
  readonly timeline: TenantHealthTimeline | null;
  readonly usage: TenantUsageHistory | null;
  readonly toolCalls: TenantToolCallHistory | null;
  readonly metrics: TenantMetrics | null;
  // Load state of the health, tool-call and metrics queries feeding the checks.
  readonly sourceStatuses: ReadonlyArray<SourceStatus>;
};

const latest = (metrics: TenantMetrics | null, metric: string): number | null => {
  const points = metrics?.series.find((s) => s.metric === metric)?.points ?? [];
  for (let i = points.length - 1; i >= 0; i--) {
    const value = points[i]?.value;
    if (value != null) return value;
  }
  return null;
};

const diskPct = (claw: AdminOpenClaw): number | null =>
  claw.disk != null && claw.disk.totalGb > 0 ? Math.round((claw.disk.usedGb / claw.disk.totalGb) * 100) : null;

const pctTone = (pct: number | null, warn: number, crit: number): StatTone | undefined =>
  pct == null ? undefined : pct >= crit ? "crit" : pct >= warn ? "warn" : undefined;

type AttentionStripProps = {
  readonly items: ReadonlyArray<AttentionItem>;
  readonly coverage: AttentionCoverage;
  readonly windowLabel: string;
};

const Unavailable = (): JSX.Element => (
  <p className={styles.allClear}><span className={styles.mutedDot} aria-hidden />Some checks unavailable</p>
);

// Never claims all-clear on data it does not have: nothing while sources load,
// and a "some checks unavailable" note when one failed.
export const AttentionStrip = ({ items, coverage, windowLabel }: AttentionStripProps): JSX.Element | null => {
  const [params] = useSearchParams();
  const linkTo = (tab: string): { search: string } => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    return { search: next.toString() };
  };
  if (items.length === 0) {
    if (coverage === "pending") return null;
    if (coverage === "partial") return <Unavailable />;
    return <p className={styles.allClear}><span className={styles.okDot} aria-hidden />Nothing flagged {windowLabel}</p>;
  }
  return (
    <div className={styles.attentionRow}>
      <ul className={styles.attention} aria-label="Needs attention">
        {items.map((item) => (
          <li key={item.key}>
            <Link to={linkTo(item.tab)} className={styles.attentionItem} data-tone={item.tone}>
              <span className={styles.attentionDot} aria-hidden />
              {item.text}
              <span className={styles.attentionGo} aria-hidden>›</span>
            </Link>
          </li>
        ))}
      </ul>
      {coverage === "partial" && <Unavailable />}
    </div>
  );
};

const plural = (n: number, word: string): string => `${n} ${word}`;

// A ChatGPT-subscription tenant has no metered chat spend; say so rather than "—".
const spendTile = (usage: TenantUsageHistory | null, claw: AdminOpenClaw): { value: string; sub?: string } => {
  const spend = usage?.latestSpentUsd ?? null;
  if (spend == null && isUnmeteredAi(claw.aiMode)) return { value: "ChatGPT", sub: "not metered" };
  if (spend == null) return { value: "—" };
  return usage?.limitUsd != null ? { value: `$${spend.toFixed(2)}`, sub: `of $${usage.limitUsd.toFixed(0)}` } : { value: `$${spend.toFixed(2)}` };
};

const Kpis = ({ claw, range, timeline, usage, toolCalls, metrics, restarts }: OverviewProps & { restarts: RestartHistory | null }): JSX.Element => {
  const scope = rangeShortLabel(range);
  const uptime = timeline?.uptimePct ?? null;
  const disk = diskPct(claw);
  const mem = latest(metrics, "mem");
  const swap = latest(metrics, "swap");
  const unexpected = restarts?.unexpectedRestartCount ?? 0;
  const failed = toolCalls?.totalError ?? 0;
  const toolTotal = toolCalls != null ? toolCalls.totalOk + toolCalls.totalError : null;
  return (
    <div className={styles.kpiRow}>
      <StatCard label="Uptime" scope={scope} value={uptime != null ? `${uptime.toFixed(1)}%` : "—"}
        tone={uptime != null && uptime < 95 ? "warn" : undefined} />
      <StatCard label="Restarts" scope={scope} value={restarts != null ? String(restarts.restartCount) : "—"}
        sub={unexpected > 0 ? plural(unexpected, "unexpected") : undefined} tone={unexpected > 0 ? "warn" : undefined} />
      <StatCard label="Tool calls" scope={scope} value={toolTotal != null ? toolTotal.toLocaleString() : "—"}
        sub={failed > 0 ? plural(failed, "failed") : undefined} />
      <StatCard label="AI spend" scope={usage?.periodLabel ?? undefined} {...spendTile(usage, claw)} />
      <StatCard label="Disk" scope="now" value={disk != null ? `${disk}%` : "—"}
        sub={claw.disk != null ? `${claw.disk.usedGb.toFixed(0)} / ${claw.disk.totalGb.toFixed(0)} GB` : undefined} tone={pctTone(disk, 80, 90)} />
      <StatCard label="Memory" scope="now" value={mem != null ? `${Math.round(mem)}%` : "—"}
        sub={swap != null ? `swap ${Math.round(swap)}%` : undefined} tone={pctTone(swap, 50, 80)} />
    </div>
  );
};

export const InstanceOverview = (props: OverviewProps): JSX.Element => {
  const { claw, range, timeline, toolCalls, metrics, sourceStatuses } = props;
  const restartQuery = useRestartHistory(claw.tenantId, range, 0);
  const restarts = restartQuery.data;
  const windowLabel = rangeWindowLabel(range);
  const items = attentionItems({
    diskPct: diskPct(claw),
    swapPct: latest(metrics, "swap"),
    uptimePct: timeline?.uptimePct ?? null,
    unexpectedRestarts: restarts?.unexpectedRestartCount ?? null,
    oomEvents: restarts?.oomEventCount ?? null,
    toolOk: toolCalls?.totalOk ?? null,
    toolError: toolCalls?.totalError ?? null,
    windowLabel,
  });
  return (
    <div className={styles.overview}>
      {claw.stopped && (
        <p className={styles.pausedNote}>Paused (EC2 stopped). Health checks are suspended; charts show activity before the pause.</p>
      )}
      <AttentionStrip items={items} coverage={attentionCoverage([...sourceStatuses, restartQuery.status])} windowLabel={windowLabel} />
      <Kpis {...props} restarts={restarts} />
    </div>
  );
};
